/* ==========================================================================
   FITRAH — scroll story

   One continuous camera move over a pinned stage, driven by scroll.

   The candle is described as five WAYPOINTS — one per beat of the story — and
   joined by a centripetal Catmull-Rom spline. The spline passes through every
   pose with a continuous tangent, so the object is never parked and never
   dashes; the segment parameter is deliberately LINEAR, because easing each
   segment would zero the velocity at every waypoint, which is the same thing
   as stopping five times. Easing belongs to the two ends of the film only.

   A single --burn value rises across the whole journey and drives the flame,
   the aura, the floor pool and the ground together, so the page reads as one
   candle burning down rather than five lit compositions in a row.

   Per frame: transform + opacity on three candle layers, two custom properties
   per chapter, one on the section, one on the ground. Nothing else.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------- INSTANCES

     The <script> tag lives inside the section, so the theme editor re-inserts
     — and therefore re-executes — this file on every edit the merchant makes.
     Re-entry must not stack a second set of window listeners on a stage that
     is about to be detached, and the section has a preset, so there can be
     more than one stage on a page. One registry, created once, owns them all.
     ------------------------------------------------------------------------ */
  if (window.fitrahStory) {
    window.fitrahStory.sweep();
    return;
  }

  var instances = [];

  function init(story) {
    if (story.dataset.storyBound === 'true') return;
    story.dataset.storyBound = 'true';

    var stage = story.querySelector('[data-story-stage]');
    var track = story.querySelector('[data-story-track]');
    var scenes = Array.prototype.slice.call(story.querySelectorAll('[data-scene]'));
    var rails = Array.prototype.slice.call(story.querySelectorAll('[data-rail]'));
    var layers = Array.prototype.slice.call(story.querySelectorAll('[data-candle-inner]'));
    var thread = story.querySelector('[data-thread]');
    var threadLine = story.querySelector('[data-thread-line]');
    var threadReveal = story.querySelector('[data-thread-reveal]');
    if (!stage || !track || !layers.length) return;

    /* ---------------------------------------------------------- CHAPTER TIME

       Each chapter of copy owns a window; the candle's matching beat sits at its
       centre. The gaps between windows are where the stage is empty and the
       object alone is crossing it — the one place the film has no words. */
    var FADE = 0.048;
    var WINDOWS = [
      { in: -0.06, out: 0.13 },  /* 0 opening    — lit from the first pixel */
      { in: 0.175, out: 0.345 }, /* 1 the object */
      { in: 0.44, out: 0.605 },  /* 2 the making */
      { in: 0.655, out: 0.83 },  /* 3 the range  */
      { in: 0.875, out: 1.08 }   /* 4 invitation — holds to the end */
    ];

    /* --------------------------------------------------------- CHOREOGRAPHY

       x / y are percentages of the candle's own box, s is scale, r is degrees.
       One entry per beat. What each move MEANS, in order:

         0.00  the object arrives — centre, full size, nearest the camera
         0.26  we come closer — it grows and moves off-centre, so the glass and
               the lid are read at something near hand distance. (A chapter about
               weight and material has to move toward the object, not away.)
         0.52  we step back into the room — it recedes into a wide pool of its
               own light, which is what ten days of resting should look like
         0.74  it holds its place while two more rise into that light beside it
         1.00  the three settle, the room at its warmest
    */
    var WAYPOINTS = {
      wide: {
        a: [
          { p: 0.0, x: 0, y: 8, s: 1.0, r: 0 },
          { p: 0.26, x: 44, y: -3, s: 0.92, r: -2.8 },
          { p: 0.52, x: -42, y: 2, s: 0.7, r: 2.8 },
          { p: 0.74, x: -68, y: 0, s: 0.53, r: 0.8 },
          { p: 1.0, x: -62, y: -4, s: 0.49, r: 0 }
        ],
        b: [
          { p: 0.0, x: 0, y: 76, s: 0.44, r: 0 },
          { p: 0.26, x: 0, y: 74, s: 0.44, r: 0 },
          { p: 0.52, x: 0, y: 52, s: 0.45, r: 0 },
          { p: 0.74, x: 0, y: 0, s: 0.53, r: 0 },
          { p: 1.0, x: 0, y: -4, s: 0.49, r: 0 }
        ],
        c: [
          { p: 0.0, x: 62, y: 78, s: 0.44, r: 0 },
          { p: 0.26, x: 62, y: 77, s: 0.44, r: 0 },
          { p: 0.52, x: 62, y: 58, s: 0.45, r: 0 },
          { p: 0.74, x: 68, y: 0, s: 0.53, r: 0 },
          { p: 1.0, x: 62, y: -4, s: 0.49, r: 0 }
        ]
      },
      /* Narrow is its own art direction, not a scaled desktop. CSS gives the
         candles the upper band; the object leans a short distance rather than
         crossing the whole stage, and the range resolves as a cascade down the
         band — three overlapping planes, which a 375px canvas can hold, instead
         of three shrunken thumbnails in a row, which it cannot. */
      narrow: {
        a: [
          { p: 0.0, x: 0, y: 20, s: 1.0, r: 0 },
          { p: 0.26, x: 15, y: 1, s: 0.97, r: -2.6 },
          { p: 0.52, x: -15, y: 3, s: 0.85, r: 2.6 },
          { p: 0.74, x: -36, y: -28, s: 0.55, r: 0.6 },
          { p: 1.0, x: -31, y: 13, s: 0.51, r: 0 }
        ],
        b: [
          { p: 0.0, x: 2, y: 68, s: 0.5, r: 0 },
          { p: 0.26, x: 2, y: 66, s: 0.5, r: 0 },
          { p: 0.52, x: 2, y: 44, s: 0.49, r: 0 },
          { p: 0.74, x: 2, y: -2, s: 0.57, r: 0 },
          { p: 1.0, x: 2, y: 29, s: 0.53, r: 0 }
        ],
        c: [
          { p: 0.0, x: 38, y: 76, s: 0.47, r: 0 },
          { p: 0.26, x: 38, y: 75, s: 0.47, r: 0 },
          { p: 0.52, x: 38, y: 60, s: 0.46, r: 0 },
          { p: 0.74, x: 39, y: 23, s: 0.54, r: 0 },
          { p: 1.0, x: 35, y: 45, s: 0.5, r: 0 }
        ]
      }
    };

    /* Two and three are not in the film until the light spreads. Opacity is a
       plain ramp rather than part of the spline: a spline through a long run of
       zeros overshoots into negative and flickers them on early. */
    var ENTRANCE = { a: null, b: { from: 0.56, to: 0.71 }, c: { from: 0.585, to: 0.735 } };

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var wide = window.matchMedia('(min-width: 900px)');

    var top = 0;
    var span = 1;
    var chapter = -1;
    var threadKey = '';
    var ticking = false;
    var ground = {};

    /* --------------------------------------------------------- GROUND PAINT

       The ground is four stacked gradients filling the whole stage, and moving
       any one of them repaints all of it. Written to three decimals these
       values change on practically every frame, which buys a full-viewport
       repaint per frame for a horizon that shifts by well under a pixel.
       Two decimals is the same picture and roughly a tenth of the repaints —
       and when the rounded value is unchanged, nothing is written at all, so
       the style is never even invalidated. */
    function setGround(name, value) {
      var next = value.toFixed(2);
      if (ground[name] === next) return;
      ground[name] = next;
      story.style.setProperty(name, next);
    }
    var lastWidth = window.innerWidth;
    /* null = neither path has been applied yet. It matters: on a first load with
       reduce-motion on, `false` here would make stop() bail before it ever put
       the stage into its static layout, leaving the chapters stacked and unread. */
    var running = null;

    function clamp(v) {
      return v < 0 ? 0 : v > 1 ? 1 : v;
    }

    function lerp(a, b, t) {
      return a + (b - a) * t;
    }

    /* Smootherstep — zero first and second derivative at both ends. Used for the
       ends of the journey and for copy fades, never between waypoints. */
    function smooth(t) {
      return t * t * t * (t * (t * 6 - 15) + 10);
    }

    /* Ease the clock at the two ends only, so the film starts and stops gently
       without coming to rest at every pose in between. */
    function clock(p) {
      if (p < 0.1) return smooth(p / 0.1) * 0.1;
      if (p > 0.92) return 0.92 + smooth((p - 0.92) / 0.08) * 0.08;
      return p;
    }

    /* Catmull-Rom through the waypoints. Ends are duplicated so the first and
       last segments have a tangent to work with. */
    function spline(pts, key, t) {
      var n = pts.length;
      var i = 0;
      while (i < n - 2 && t > pts[i + 1].p) i++;
      var p0 = pts[i - 1 < 0 ? 0 : i - 1];
      var p1 = pts[i];
      var p2 = pts[i + 1];
      var p3 = pts[i + 2 > n - 1 ? n - 1 : i + 2];
      var u = clamp((t - p1.p) / (p2.p - p1.p || 1));
      var v0 = p0[key], v1 = p1[key], v2 = p2[key], v3 = p3[key];
      var u2 = u * u;
      return 0.5 * (2 * v1 + (-v0 + v2) * u + (2 * v0 - 5 * v1 + 4 * v2 - v3) * u2 +
        (-v0 + 3 * v1 - 3 * v2 + v3) * u2 * u);
    }

    function entranceAt(key, p) {
      var e = ENTRANCE[key];
      if (!e) return 1;
      return smooth(clamp((p - e.from) / (e.to - e.from)));
    }

    /* ------------------------------------------------------------- THE THREAD

       Derived from the choreography rather than drawn by hand: the height at each
       step is the level the candle's base sits at during that beat, laid out
       left to right across the stage. So it is the surface the object travels
       along — it remembers the journey without tracing it literally, which would
       double back on itself and read as a scribble. */
    function threadPath() {
      var set = WAYPOINTS[wide.matches ? 'wide' : 'narrow'].a;
      var box = story.querySelector('[data-candle="a"]');
      var inner = layers[0];
      if (!box || !inner) return '';
      var sr = stage.getBoundingClientRect();
      var br = box.getBoundingClientRect();
      if (!sr.height || !inner.offsetHeight) return '';

      var pts = [];
      for (var i = 0; i < set.length; i++) {
        var w = set[i];
        /* Base of the candle at this beat, in stage percent. */
        var cy = (br.top + br.height / 2) - sr.top +
          (w.y / 100) * inner.offsetHeight +
          (inner.offsetHeight * w.s) / 2;
        pts.push([(i / (set.length - 1)) * 116 - 8, (cy / sr.height) * 100 + 4]);
      }

      /* Catmull-Rom control points expressed as cubic Beziers. */
      var d = 'M ' + pts[0][0].toFixed(1) + ' ' + pts[0][1].toFixed(1);
      for (var j = 0; j < pts.length - 1; j++) {
        var a = pts[j - 1 < 0 ? 0 : j - 1];
        var b = pts[j];
        var c = pts[j + 1];
        var e = pts[j + 2 > pts.length - 1 ? pts.length - 1 : j + 2];
        d += ' C ' + (b[0] + (c[0] - a[0]) / 6).toFixed(1) + ' ' + (b[1] + (c[1] - a[1]) / 6).toFixed(1) +
          ', ' + (c[0] - (e[0] - b[0]) / 6).toFixed(1) + ' ' + (c[1] - (e[1] - b[1]) / 6).toFixed(1) +
          ', ' + c[0].toFixed(1) + ' ' + c[1].toFixed(1);
      }
      return d;
    }

    function measure() {
      top = track.getBoundingClientRect().top + window.scrollY;
      span = Math.max(1, track.offsetHeight - stage.offsetHeight);
      lastWidth = window.innerWidth;
      threadKey = '';
    }

    function render() {
      ticking = false;

      var p = clamp((window.scrollY - top) / span);
      var t = clock(p);
      var narrow = !wide.matches;
      var set = WAYPOINTS[narrow ? 'narrow' : 'wide'];
      var i;

      for (i = 0; i < layers.length; i++) {
        var layer = layers[i];
        var key = layer.getAttribute('data-candle-inner');
        var pts = set[key];
        if (!pts) continue;
        layer.style.transform =
          'translate3d(' + spline(pts, 'x', t).toFixed(2) + '%,' + spline(pts, 'y', t).toFixed(2) + '%,0) ' +
          'scale(' + spline(pts, 's', t).toFixed(3) + ') rotate(' + spline(pts, 'r', t).toFixed(2) + 'deg)';
        layer.style.opacity = (entranceAt(key, p) * (1 - clamp((p - 0.94) / 0.06) * 0.06)).toFixed(3);
      }

      /* Chapters. Each eases in, holds, eases out; the drift is signed, so copy
         arrives from below and leaves upward instead of bouncing back. */
      var lead = 0;
      var leadAt = -1;
      for (i = 0; i < scenes.length; i++) {
        var w = WINDOWS[i];
        if (!w) continue;
        var sp;
        var drift;
        if (p < w.in) {
          sp = 0;
          drift = 1;
        } else if (p < w.in + FADE) {
          sp = smooth((p - w.in) / FADE);
          drift = 1 - sp;
        } else if (p < w.out - FADE) {
          sp = 1;
          drift = 0;
        } else if (p < w.out) {
          sp = 1 - smooth((p - (w.out - FADE)) / FADE);
          drift = -(1 - sp) * 0.8;
        } else {
          sp = 0;
          drift = -0.8;
        }
        scenes[i].style.setProperty('--sp', sp.toFixed(3));
        scenes[i].style.setProperty('--sd', drift.toFixed(3));
        var live = sp > 0.004;
        if (live !== (scenes[i].getAttribute('data-active') === 'true')) {
          scenes[i].setAttribute('data-active', live ? 'true' : 'false');
        }
        if (sp > leadAt) {
          leadAt = sp;
          lead = i;
        }
      }
      /* Between chapters nothing is lit; the rail holds where it was rather than
         snapping back to the first tick. */
      if (leadAt <= 0.004) lead = chapter < 0 ? 0 : chapter;

      /* One candle burning down: flame, aura, floor pool and ground all rise on
         this single value, so light accumulates across the whole page instead of
         each chapter being separately lit. */
      story.style.setProperty('--burn', smooth(clamp(p / 0.9)).toFixed(3));
      setGround('--gp', p);
      /* The closing swell: warmth rises from the floor under the invitation so
         the handoff into the cream sections below reads as light, not a cut. */
      setGround('--gf', clamp((p - 0.84) / 0.16));

      if (thread && threadLine && threadReveal) {
        var mode = narrow ? 'narrow' : 'wide';
        if (mode !== threadKey) {
          var d = threadPath();
          if (d) {
            threadKey = mode;
            threadLine.setAttribute('d', d);
            threadReveal.setAttribute('d', d);
          }
        }
        /* Draws on across the journey, then resolves before the invitation. */
        threadReveal.style.strokeDashoffset = (1 - clamp((p - 0.04) / 0.74)).toFixed(4);
        /* Faint at first, a little more present as the story accumulates, gone by
           the time the CTA is the only thing left. */
        var fade = p < 0.06 ? 0 : p < 0.2 ? (p - 0.06) / 0.14 : p < 0.82 ? 1 : (0.94 - p) / 0.12;
        thread.style.opacity = (clamp(fade) * lerp(0.55, 1, clamp(p / 0.8))).toFixed(3);
      }

      if (lead !== chapter) {
        if (rails[chapter]) rails[chapter].setAttribute('data-current', 'false');
        if (rails[lead]) rails[lead].setAttribute('data-current', 'true');
        chapter = lead;
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(render);
    }

    function onResize() {
      measure();
      onScroll();
    }

    /* --------------------------------------------------- VIEWPORT, NOT CHROME

       The stage is 100dvh so it always fills the screen, which is what keeps the
       composition whole on a phone. The cost is that iOS Safari and Chrome on
       Android collapse and expand the URL bar mid-scroll, and every one of those
       fires `resize` with the width unchanged. Re-measuring there recomputes
       `span` against a stage that just grew by the height of the browser chrome,
       and the scrub jumps under the reader's thumb — mid-gesture, which is
       exactly when it is most visible.

       Width is the honest signal for a layout change: a rotation, a desktop drag,
       a split view. A height-only resize is chrome, so hold the measurement and
       just paint the current frame. Real layout changes still come through
       orientationchange, font loading and load. */
    function onViewportResize() {
      if (window.innerWidth === lastWidth) {
        onScroll();
        return;
      }
      onResize();
    }

    function start() {
      if (running === true) return;
      running = true;
      story.classList.remove('story--static');
      chapter = -1;
      measure();
      render();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onViewportResize);
      window.addEventListener('orientationchange', onResize);
      window.addEventListener('load', onResize);
      /* Web fonts change the stage height once they land */
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(onResize);
    }

    function stop() {
      if (running === false) return;
      running = false;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onViewportResize);
      window.removeEventListener('orientationchange', onResize);
      window.removeEventListener('load', onResize);
      story.classList.add('story--static');
      layers.forEach(function (layer) {
        layer.style.transform = '';
        layer.style.opacity = '';
      });
      story.style.removeProperty('--burn');
      story.style.removeProperty('--gp');
      story.style.removeProperty('--gf');
      ground = {};
      if (thread) thread.style.opacity = '';
      /* The static fallback is an ordinary stacked article: every chapter is
         readable at once, so clear everything the scrubber wrote. */
      scenes.forEach(function (scene) {
        scene.style.removeProperty('--sp');
        scene.style.removeProperty('--sd');
        scene.setAttribute('data-active', 'true');
      });
      rails.forEach(function (rail) {
        rail.setAttribute('data-current', 'false');
      });
      chapter = -1;
    }

    function sync() {
      if (reduceMotion.matches) stop();
      else start();
    }

    if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', sync);
    else if (reduceMotion.addListener) reduceMotion.addListener(sync);

    sync();

    instances.push({
      story: story,
      remeasure: onResize,
      destroy: function () {
        stop();
        if (reduceMotion.removeEventListener) reduceMotion.removeEventListener('change', sync);
        else if (reduceMotion.removeListener) reduceMotion.removeListener(sync);
      }
    });
  }

  /* Retire instances whose section markup has been replaced, adopt anything
     new, and re-measure the survivors — the editor changes layout above them. */
  function sweep() {
    instances = instances.filter(function (instance) {
      if (document.contains(instance.story)) return true;
      instance.destroy();
      return false;
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-story]'), init);
    instances.forEach(function (instance) {
      instance.remeasure();
    });
  }

  window.fitrahStory = { sweep: sweep };

  sweep();

  /* Theme editor: adopt, retire and re-measure as the merchant edits */
  document.addEventListener('shopify:section:load', sweep);
  document.addEventListener('shopify:section:unload', sweep);
})();
